import { makeAutoObservable, runInAction } from 'mobx'
import agent from '../api/agent'
import { FirearmBuild, FirearmBuildFormValues } from '../models/build'

export type BuildDetailLoadStatus = 'idle' | 'loading' | 'ready' | 'not-found'

export default class BuildStore {
  buildRegistry = new Map<string, FirearmBuild>()
  selectedBuild: FirearmBuild | undefined = undefined
  selectedBuildLoadStatus: BuildDetailLoadStatus = 'idle'
  loading = false
  loadingInitial = false

  constructor () {
    makeAutoObservable(this)
  }

  get buildsByDate () {
    return Array.from(this.buildRegistry.values()).sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
  }

  loadBuilds = async (firearmModelId?: string, ownerId?: string, params?: { tagIds?: string[]; tagMatch?: string }) => {
    this.loadingInitial = true
    try {
      const result = await agent.Builds.list({
        firearmModelId,
        ownerId,
        tagIds: params?.tagIds,
        tagMatch: params?.tagMatch ?? 'any'
      })
      runInAction(() => {
        this.buildRegistry.clear()
        result.items.forEach((b) => this.buildRegistry.set(b.id, b))
        this.loadingInitial = false
      })
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loadingInitial = false))
    }
  }

  loadBuild = async (id: string) => {
    this.loading = true
    this.selectedBuildLoadStatus = 'loading'
    try {
      const build = await agent.Builds.details(id)
      runInAction(() => {
        this.selectedBuild = build
        this.buildRegistry.set(build.id, build)
        this.loading = false
        this.selectedBuildLoadStatus = 'ready'
      })
      return build
    } catch (error) {
      runInAction(() => {
        this.selectedBuild = undefined
        this.loading = false
        this.selectedBuildLoadStatus = 'not-found'
      })
      throw error
    }
  }

  loadBuildBySlug = async (username: string, slug: string) => {
    this.loading = true
    this.selectedBuildLoadStatus = 'loading'
    try {
      const build = await agent.Builds.detailsBySlug(username, slug)
      runInAction(() => {
        this.selectedBuild = build
        this.buildRegistry.set(build.id, build)
        this.loading = false
        this.selectedBuildLoadStatus = 'ready'
      })
      return build
    } catch (error) {
      runInAction(() => {
        this.selectedBuild = undefined
        this.loading = false
        this.selectedBuildLoadStatus = 'not-found'
      })
      throw error
    }
  }

  createBuild = async (values: FirearmBuildFormValues) => {
    this.loading = true
    try {
      const result = await agent.Builds.create(values)
      runInAction(() => (this.loading = false))
      return result?.id
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  updateBuild = async (id: string, values: {
    name?: string
    description?: string
    visibility?: string
    allowDuplication?: boolean
    purpose?: string
  }) => {
    this.loading = true
    try {
      await agent.Builds.update(id, values)
      runInAction(() => {
        if (this.selectedBuild?.id === id) {
          const next = { ...this.selectedBuild, ...values }
          if (values.visibility != null) {
            next.visibility = values.visibility as FirearmBuild['visibility']
            next.isPublic = values.visibility !== 'Private'
          }
          if (values.allowDuplication != null) next.allowDuplication = values.allowDuplication
          if (values.purpose !== undefined) next.purpose = values.purpose || null
          this.selectedBuild = next
        }
        const existing = this.buildRegistry.get(id)
        if (existing) {
          const next = { ...existing, ...values }
          if (values.visibility != null) {
            next.visibility = values.visibility as FirearmBuild['visibility']
            next.isPublic = values.visibility !== 'Private'
          }
          if (values.allowDuplication != null) next.allowDuplication = values.allowDuplication
          if (values.purpose !== undefined) next.purpose = values.purpose || null
          this.buildRegistry.set(id, next)
        }
        this.loading = false
      })
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  deleteBuild = async (id: string) => {
    this.loading = true
    try {
      await agent.Builds.delete(id)
      runInAction(() => {
        this.buildRegistry.delete(id)
        if (this.selectedBuild?.id === id) this.selectedBuild = undefined
        this.loading = false
      })
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  addPartToBuild = async (buildId: string, partId: string, notes?: string, quantity?: number, zoneOverride?: string) => {
    this.loading = true
    try {
      await agent.Builds.addPart(buildId, partId, notes, quantity, zoneOverride)
      runInAction(() => (this.loading = false))
      await this.loadBuild(buildId)
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  updateBuildPart = async (buildId: string, buildPartId: string, notes?: string, quantity?: number, zoneOverride?: string) => {
    this.loading = true
    try {
      await agent.Builds.updatePart(buildId, buildPartId, notes, quantity, zoneOverride)
      runInAction(() => (this.loading = false))
      await this.loadBuild(buildId)
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  /** Persists a drag-end position and updates only the affected part locally — no full reload. */
  updateBuildPartPosition = async (
    buildId: string,
    buildPartId: string,
    x: number,
    y: number
  ) => {
    await agent.Builds.updatePart(buildId, buildPartId, undefined, undefined, undefined, {
      xOffsetPercent: x,
      yOffsetPercent: y
    })
    runInAction(() => {
      if (!this.selectedBuild || this.selectedBuild.id !== buildId) return
      // Update in the flat parts list.
      const part = this.selectedBuild.parts?.find((p) => p.id === buildPartId)
      if (part) { part.xOffsetPercent = x; part.yOffsetPercent = y }
      // Update in partsByZone (same objects, but iterate defensively).
      if (this.selectedBuild.partsByZone) {
        for (const zoneParts of Object.values(this.selectedBuild.partsByZone)) {
          const zp = zoneParts.find((p) => p.id === buildPartId)
          if (zp) { zp.xOffsetPercent = x; zp.yOffsetPercent = y; break }
        }
      }
    })
  }

  movePartToZone = async (
    buildId: string,
    buildPartId: string,
    zoneId: string,
    zoneRelativeX: number,
    zoneRelativeY: number
  ) => {
    await agent.Builds.movePart(buildId, buildPartId, zoneId, zoneRelativeX, zoneRelativeY)
    runInAction(() => {
      if (!this.selectedBuild || this.selectedBuild.id !== buildId) return
      // Update in the flat parts list.
      const part = this.selectedBuild.parts?.find((p) => p.id === buildPartId)
      if (part) {
        part.zoneId = zoneId
        part.zoneRelativeXPercent = zoneRelativeX
        part.zoneRelativeYPercent = zoneRelativeY
      }
      // Update in partsByZone (same objects, but iterate defensively).
      if (this.selectedBuild.partsByZone) {
        for (const zoneParts of Object.values(this.selectedBuild.partsByZone)) {
          const zp = zoneParts.find((p) => p.id === buildPartId)
          if (zp) {
            zp.zoneId = zoneId
            zp.zoneRelativeXPercent = zoneRelativeX
            zp.zoneRelativeYPercent = zoneRelativeY
            break
          }
        }
      }
    })
  }

  removePartFromBuild = async (buildId: string, partId: string) => {
    this.loading = true
    try {
      await agent.Builds.removePart(buildId, partId)
      runInAction(() => (this.loading = false))
      await this.loadBuild(buildId)
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  clearSelectedBuild = () => {
    this.selectedBuild = undefined
    this.selectedBuildLoadStatus = 'idle'
  }
}
